import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * v2 · 04 Inputs — 52px, radius 10, label above.
 *
 * 16px type in a faint white-alpha well. States:
 *   empty    — hairline border, placeholder in ink-faint
 *   filled   — text goes ink; the border stays quiet
 *   focus    — the hairline swaps for a solid ink border, no glow
 *   invalid  — destructive border over a destructive wash
 *   disabled — sunk surface, 0.7 opacity
 */
export const fieldBox = [
  'w-full rounded-sm border bg-[color:var(--sss-field-bg)] text-ink',
  'border-[color:var(--sss-field-border)]',
  'font-sans text-[16px]',
  'transition-[border-color,background-color] duration-[120ms] ease-standard',
  'placeholder:text-ink-faint',
  // Focus swaps the hairline for a solid ink border — no glow rings.
  'focus-visible:outline-none focus-visible:border-ink',
  'aria-invalid:border-destructive aria-invalid:bg-[color-mix(in_srgb,var(--sss-destructive)_6%,transparent)]',
  'aria-invalid:focus-visible:border-destructive',
  'disabled:cursor-not-allowed disabled:border-hairline disabled:bg-surface-sunk disabled:text-ink-faint disabled:opacity-70',
].join(' ')

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, type, placeholder, ...props }, ref) => (
    <input
      type={type}
      ref={ref}
      placeholder={placeholder ?? ' '}
      className={cn(fieldBox, 'h-13 px-4', className)}
      {...props}
    />
  )
)
Input.displayName = 'Input'

/**
 * Numeric input — same family (Inter does everything now), tabular
 * figures. Card numbers, order IDs, tracking numbers.
 */
const NumericInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => (
    <Input ref={ref} inputMode="numeric" className={cn('tabular-nums', className)} {...props} />
  )
)
NumericInput.displayName = 'NumericInput'

export { Input, NumericInput }
